import { useState, useEffect, useRef, useCallback, useId } from "react";
import { useFetch } from "use-http";
import {
  PipelineTemplateSelect,
  type PipelineTemplate,
} from "~/components/pipeline/PipelineTemplateSelect";
import {
  SCMProviderType,
  type RepositorySelectionEventDetail,
} from "~/components/pipeline/New/ConnectGitPicker/util/types";
import FormYAMLEditorFieldLoader, {
  CodeMirrorInstance,
} from "~/components/shared/FormYAMLEditorField";
import StepsEditorSidebar, {
  StepsEditorSidebarSection,
} from "~/components/shared/StepsEditorSidebar";
import cn from "classnames";
import Icon from "~/components/shared/Icon";
import Spinner from "~/components/shared/Spinner";

export type Props = {
  currentConfiguration?: string;
  currentPipelineTemplateUuid: string | null;
  pipelineTemplates: Array<PipelineTemplate>;
  pipelineTemplatesRequired: boolean;
  errors: string[];
  repositoryProvidersUrl?: string;
  organizationUuid?: string;
  showEditableMessage?: boolean;
  inputNames: {
    privateTemplate: string;
    publicTemplate: string;
    yamlEditor: string;
  };
};

/**
 * Steps YAML editor with template selector.
 */
const StepsEditor = ({
  currentConfiguration,
  currentPipelineTemplateUuid,
  pipelineTemplates,
  pipelineTemplatesRequired: orgTemplatesRequired,
  errors: validationErrors,
  repositoryProvidersUrl,
  organizationUuid,
  inputNames,
  showEditableMessage = true,
}: Props) => {
  const [errors, setErrors] = useState<string[]>(validationErrors);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    setErrors(validationErrors);
  }, [validationErrors]);

  // Set the initial selected templated state based on server-side props.
  const [selectedTemplate, setSelectedTemplate] = useState<PipelineTemplate | null>(() => {
    const byUuid = pipelineTemplates.find(
      (template) => template.uuid === currentPipelineTemplateUuid,
    );
    if (byUuid) {
      return byUuid;
    }

    if (!currentConfiguration) {
      // Prefer a "public" template, fall back to "private" if none found
      let defaultTemplate = pipelineTemplates.find((template) => template.type === "public");
      if (!defaultTemplate) {
        defaultTemplate = pipelineTemplates.find((template) => template.type === "private");
      }
      return defaultTemplate || null;
    }

    return null;
  });
  // Track how the template was selected:
  // "repository" means auto-selected from repo picker,
  // "manual" means user chose from template dropdown.
  // Used to determine whether the editor should be read-only in PB-747.
  const [templateSelectionSource, setTemplateSelectionSource] = useState<
    "repository" | "manual" | null
  >(null);

  const [haveCheckedRepositoryForConfig, setHaveCheckedRepositoryForConfig] = useState(false);
  const [pipelineConfig, setPipelineConfig] = useState<{ path: string; url: string } | null>(null);
  const codeMirrorInstanceRef = useRef<CodeMirrorInstance | null>(null);

  const isEditable =
    !orgTemplatesRequired &&
    (selectedTemplate?.editable || selectedTemplate === null) &&
    templateSelectionSource !== "repository";

  const onTemplateSelect = useCallback(
    (template: PipelineTemplate | null, source: "repository" | "manual" = "manual") => {
      setSelectedTemplate(template);
      setTemplateSelectionSource(source);
      setErrors([]);

      if (template) {
        codeMirrorInstanceRef.current?.setValue(template.configuration);
      } else {
        codeMirrorInstanceRef.current?.setValue("");
      }
    },
    [],
  );

  const { get, response, loading, error } = useFetch<{ found_configs: string[] }>();

  // Unlock template selector when non-example template becomes selected while locked
  // This handles state-driven template changes (e.g provider switch resetting to default template at line 160)
  // The unlock in handleRepositoryChange handles repository selection events
  useEffect(() => {
    if (
      selectedTemplate &&
      templateSelectionSource === "repository" &&
      selectedTemplate.type !== "example"
    ) {
      setTemplateSelectionSource(null);
    }
  }, [selectedTemplate, templateSelectionSource]);

  // Handle repository selection events and update template accordingly
  useEffect(() => {
    // Skip if org templates required - public templates won't be available
    // Also won't happen on steps edit page, as there's no repositoryProvidersUrl
    if (!Features.UseRedesignedNewPipelinePage || orgTemplatesRequired || !repositoryProvidersUrl) {
      return;
    }

    const handleRepositoryChange = async (event: Event) => {
      const { repository, scmService } = (event as CustomEvent<RepositorySelectionEventDetail>)
        .detail;

      setPipelineConfig(null);
      setHaveCheckedRepositoryForConfig(false);

      if (!repository) {
        // No repository means the repository selection was cleared so we'll reset to the
        // default template and unlock if it was locked from example selection
        if (templateSelectionSource === "repository") {
          setTemplateSelectionSource(null);
        }
        onTemplateSelect(pipelineTemplates[0]);
        return;
      }

      // When switching from examples to another provider, unlock template selector
      if (scmService?.uuid !== "examples" && templateSelectionSource === "repository") {
        setTemplateSelectionSource(null);
      }

      // When an example repo is selected, find and select the matching template
      if (scmService?.uuid === "examples" && repository.configuration) {
        const matchingTemplate = pipelineTemplates.find(
          (template) => template.type === "example" && template.uuid === repository.slug,
        );
        onTemplateSelect(matchingTemplate || null, "repository");
        codeMirrorInstanceRef.current?.setValue(repository.configuration);
        return;
      }

      if (scmService?.provider_type !== SCMProviderType.GithubCodeAccessApp) {
        // For now we only support looking up config files in GitHub repositories, and for
        // that we need code access
        return;
      }

      const params = new URLSearchParams({
        repository: repository.clone_url,
      });
      const { found_configs } = await get(
        `${repositoryProvidersUrl}/${scmService.uuid}/repository_buildkite_configs.json?${params.toString()}`,
      );

      setHaveCheckedRepositoryForConfig(true);

      if (response.ok) {
        if (found_configs.length > 0) {
          // We found a config file in the repo - select "Pipeline Upload" template
          const pipelineUploadTemplate = pipelineTemplates.find(
            // Public templates don't have a UUID, for now they've been populated with a
            // slug for convenience, but we might want to revisit this at a later date
            (template) => template.uuid === "pipeline-upload",
          );

          const path = found_configs[0];
          const pipelineConfig = {
            path,
            url: `${repository.html_url}/tree/${repository.default_branch}/${path}`,
          };

          setPipelineConfig(pipelineConfig);
          if (pipelineUploadTemplate) {
            onTemplateSelect(pipelineUploadTemplate);
          }
        } else {
          setPipelineConfig(null);
          const helloWorldTemplate = pipelineTemplates.find(
            (template) => template.uuid === "hello-world",
          );
          if (helloWorldTemplate) {
            onTemplateSelect(helloWorldTemplate);
          }
        }
      } else if (error) {
        // Fall back to the "Hello World" template if the API call errors
        setPipelineConfig(null);
        const helloWorldTemplate = pipelineTemplates.find(
          (template) => template.uuid === "hello-world",
        );
        if (helloWorldTemplate) {
          onTemplateSelect(helloWorldTemplate);
        }
      }
    };

    document.addEventListener("pipeline:new:repositorySelected", handleRepositoryChange);
    return () =>
      document.removeEventListener("pipeline:new:repositorySelected", handleRepositoryChange);
  }, [orgTemplatesRequired, repositoryProvidersUrl, pipelineTemplates, onTemplateSelect]);

  // Handler for CodeMirror change events
  const handleEditorChange = useCallback(
    (_cm, changeObject) => {
      setErrors([]);

      if (orgTemplatesRequired) {
        return;
      }

      // Value was updated by the user, so we set template to None
      if (changeObject?.origin !== "setValue") {
        setSelectedTemplate(null);
      }
    },
    [orgTemplatesRequired],
  );

  // Handler for CodeMirror blur events
  const handleEditorBlur = useCallback((cm) => {
    if (cm.getValue() === "") {
      setErrors(["Please fill in required field"]);
    }
  }, []);

  const errorId = useId();

  const renderRepositoryConfigNotice = () => {
    if (loading) {
      return (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-gray-400 bg-gray-100 p-3 text-gray-800">
          <Spinner size={24} color={false} /> Checking repository for pipeline configuration...
        </div>
      );
    } else if (error && haveCheckedRepositoryForConfig) {
      // This will only happen if we have trouble talking to our internal API, not the GitHub API
      // Errors don't get cleared until we fire another request so we need to include
      // `haveCheckedRepositoryForConfig` in this check
      return (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-gray-400 bg-gray-100 p-3 text-gray-800">
          <Icon icon="heroicons/outline/exclamation-triangle" />
          <span className="text-sm">
            Something went wrong checking your repository for a pipeline configuration
          </span>
        </div>
      );
    } else if (pipelineConfig) {
      return (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-green-200 bg-green-100 p-3 text-green-800">
          <Icon icon="heroicons/outline/check-circle" />
          <span className="text-sm">
            Found pipeline in repository:{" "}
            <span className="inline-flex items-center gap-0.5">
              <a
                href={pipelineConfig.url}
                className="text-green-800 underline"
                target="_blank"
                rel="noreferrer"
              >
                {pipelineConfig.path}
              </a>
              <Icon icon="heroicons/16/solid/arrow-top-right-on-square" className="size-4" />
            </span>
          </span>
        </div>
      );
    } else if (haveCheckedRepositoryForConfig) {
      return (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-gray-400 bg-gray-100 p-3 text-gray-800">
          <Icon icon="heroicons/outline/information-circle" />
          <span className="text-sm">
            No pipeline configuration found in the selected repository
          </span>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {renderRepositoryConfigNotice()}
      <section
        aria-invalid={errors.length > 0 ? true : undefined}
        aria-errormessage={errors.length ? errorId : undefined}
        className={cn(
          "border-gray group/section shadow-depth-100 relative h-full max-h-[600px] min-h-[300px] rounded-md border border-transparent aria-[invalid=true]:border-red-500",
        )}
      >
        <div className={cn("flex max-h-[600px] flex-col", { "bg-silver": !isEditable })}>
          <header className="flex h-fit items-center justify-between px-3 pt-3">
            <div className="flex flex-row flex-wrap items-center gap-1">
              <h3 className="m-0 truncate p-0 text-sm font-medium leading-normal text-black group-aria-[invalid=true]/section:text-red-500">
                YAML Steps editor
              </h3>
            </div>
            <div className="flex items-center gap-3">
              {organizationUuid && !orgTemplatesRequired && (
                <button
                  type="button"
                  onClick={() => setShowGuide(!showGuide)}
                  className={cn(
                    "flex h-7 items-center gap-1.5 rounded-md px-2 font-medium leading-none hover:bg-gray-300",
                    showGuide ? "bg-purple-100 text-purple-600" : "",
                  )}
                >
                  <Icon icon="heroicons/outline/book-open" className="size-4" />
                  Guide
                </button>
              )}
              <PipelineTemplateSelect
                selected={selectedTemplate}
                templates={pipelineTemplates}
                orgTemplatesRequired={orgTemplatesRequired}
                disabled={templateSelectionSource === "repository"}
                onTemplateSelect={onTemplateSelect}
                inputNames={inputNames}
              />
            </div>
          </header>

          {orgTemplatesRequired && (
            <div className="shadow-depth-150 text-charcoal-800 mx-3 mt-2 flex h-fit flex-row items-center gap-1 rounded-sm border-2 border-white bg-orange-100 p-2">
              <Icon icon="heroicons/16/solid/document-text" className="h-4 w-4 text-[#B85D0E]" />
              Your Organization has required a template to be used for this pipeline, and editing is
              disabled.
            </div>
          )}

          {templateSelectionSource === "repository" && selectedTemplate && (
            <div className="shadow-depth-150 text-charcoal-800 mx-3 mt-2 flex h-fit flex-row items-center gap-1 rounded-sm border-2 border-white bg-blue-100 p-2">
              <Icon icon="heroicons/outline/information-circle" className="h-5 w-5 text-blue-600" />
              <div className="flex-1 text-sm">
                This pipeline will upload and run the following config from the example
                repository&apos;s pipeline.yml file.
                {selectedTemplate.type === "example" && (
                  <a
                    href={`https://github.com/buildkite/${selectedTemplate.uuid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-1 text-purple-600 hover:text-purple-700"
                  >
                    View example repo →
                  </a>
                )}
              </div>
            </div>
          )}

          <div
            className="flex h-full flex-1 overflow-auto py-1"
            data-ph-capture-attribute-name="yaml-steps-editor"
            onClick={(event) => {
              // Focus editor when clicking on wrapper (not on CodeMirror itself)
              if (
                event.target === event.currentTarget &&
                codeMirrorInstanceRef.current &&
                !orgTemplatesRequired
              ) {
                codeMirrorInstanceRef.current.focus();
                // Position cursor at the end
                const lastLine = codeMirrorInstanceRef.current.lastLine();
                const lineInfo = codeMirrorInstanceRef.current.lineInfo(lastLine);
                codeMirrorInstanceRef.current.setCursor(lastLine, lineInfo.text.length);
              }
            }}
          >
            <FormYAMLEditorFieldLoader
              key="stable-editor"
              name={
                templateSelectionSource === "repository" && selectedTemplate?.type === "example"
                  ? ""
                  : inputNames.yamlEditor
              }
              value={selectedTemplate?.configuration || currentConfiguration || ""}
              readOnly={!isEditable}
              lineWrapping={true}
              inputRequired={true}
              onChange={handleEditorChange}
              onBlur={handleEditorBlur}
              codeMirrorInstanceRef={codeMirrorInstanceRef}
            />
            {templateSelectionSource === "repository" && selectedTemplate?.type === "example" && (
              <input
                type="hidden"
                name={inputNames.yamlEditor}
                value={`steps:
  - label: ":pipeline:"
    command: buildkite-agent pipeline upload`}
              />
            )}
          </div>

          {errors.map((error) => (
            <div className="FormField__Error px-3" key={error} id={errorId}>
              {error}
            </div>
          ))}
        </div>

        {showGuide && organizationUuid && (
          <div
            className="border-gray absolute right-0 top-0 z-10 h-full max-h-[600px] w-[70%] max-w-[70%] border-l bg-white xl:max-h-full"
            data-testid="steps-editor-sidebar"
          >
            <button
              onClick={() => setShowGuide(false)}
              className="absolute right-1 top-3 z-10 rounded-full bg-gray-200"
              aria-label="Close guide"
            >
              <Icon icon="close" className="size-5 text-gray-800" />
            </button>
            <div className="relative h-full w-full overflow-scroll p-3 pr-8">
              <StepsEditorSidebar
                trackingContext={{ organizationUuid }}
                activeSection={StepsEditorSidebarSection.Guide}
                examplesPath=""
              />
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default StepsEditor;
